import { useEffect, useState, useMemo } from "react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, EmptyState, StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { CourseCombobox } from "@/components/CourseCombobox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Plus, ChevronLeft, ChevronRight, MapPin, User, Users, Video, Building2,
  Layers, Monitor, CalendarDays, Clock, List, Trash2, Upload, Search, Pencil, UserPlus, ArrowRightLeft, X,
} from "lucide-react";
import { fmtDate, fmtTime } from "@/lib/helpers";
import { stripHtml } from "@/components/RichText";

const modeIcon = { online: Monitor, classroom: Building2, blended: Layers, virtual: Video };

export default function Sessions() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState(null);
  const [myEnr, setMyEnr] = useState([]);
  const [courses, setCourses] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [view, setView] = useState("calendar");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [candidateFor, setCandidateFor] = useState(null);
  const [q, setQ] = useState("");
  const [cursor, setCursor] = useState(new Date());
  const [open, setOpen] = useState(false);
  const canManage = user.role === "admin";

  const load = async () => {
    const reqs = [api.get("/sessions"), api.get("/courses")];
    if (user.role === "employee") reqs.push(api.get("/enrollments/me"));
    if (canManage) reqs.push(api.get("/users/trainers"));
    const res = await Promise.all(reqs);
    setSessions(res[0].data);
    setCourses(res[1].data);
    if (user.role === "employee") setMyEnr(res[2].data);
    if (canManage) setTrainers(res[res.length - 1].data);
  };
  useEffect(() => { load(); }, []);

  const enrolledSessionIds = useMemo(() => new Set(myEnr.map((e) => e.session_id)), [myEnr]);

  const register = async (sessionId) => {
    try {
      const { data } = await api.post("/enrollments", { session_id: sessionId });
      toast.success(data.status === "waitlisted" ? "Added to waitlist" : "You're registered!");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (sessionId) => {
    if (!window.confirm("Delete this session? All its registrations will be removed. This cannot be undone.")) return;
    try {
      await api.delete(`/sessions/${sessionId}`);
      toast.success("Session deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (!sessions) return <Loading />;

  const startEdit = (s) => { setEditing(s); setOpen(true); };

  const sq = q.trim().toLowerCase();
  const shown = sessions.filter((s) => !sq
    || (s.course?.title || "").toLowerCase().includes(sq)
    || (s.location || "").toLowerCase().includes(sq)
    || (s.trainer_name || "").toLowerCase().includes(sq));

  return (
    <div>
      <PageHeader title="Training Sessions" subtitle="Schedule, browse and register for upcoming training." testid="sessions-header">
        <div className="flex rounded-lg border border-border overflow-hidden">
          <button onClick={() => setView("calendar")} data-testid="view-calendar-button" className={`px-3 py-1.5 text-sm flex items-center gap-1.5 ${view === "calendar" ? "bg-primary text-white" : "hover:bg-accent/40"}`}><CalendarDays className="h-4 w-4" />Calendar</button>
          <button onClick={() => setView("list")} data-testid="view-list-button" className={`px-3 py-1.5 text-sm flex items-center gap-1.5 ${view === "list" ? "bg-primary text-white" : "hover:bg-accent/40"}`}><List className="h-4 w-4" />List</button>
        </div>
        {canManage && <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="bulk-upload-sessions-button"><Upload className="h-4 w-4 mr-1.5" />Bulk upload</Button>}
        {canManage && <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="schedule-session-button"><Plus className="h-4 w-4 mr-1.5" />Schedule</Button>}
      </PageHeader>

      <div className="relative max-w-sm mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by course, location or trainer..." className="pl-9" data-testid="search-sessions-input" />
      </div>

      {view === "calendar" ? (
        <CalendarView sessions={shown} cursor={cursor} setCursor={setCursor} enrolledSessionIds={enrolledSessionIds} register={register} user={user} canManage={canManage} remove={remove} onEdit={startEdit} onAddCandidate={setCandidateFor} reload={load} />
      ) : (
        <ListView sessions={shown} enrolledSessionIds={enrolledSessionIds} register={register} user={user} canManage={canManage} remove={remove} reload={load} onEdit={startEdit} onAddCandidate={setCandidateFor} />
      )}

      {canManage && <SessionDialog open={open} setOpen={setOpen} session={editing} courses={courses} trainers={trainers} onSaved={load} isAdmin={user.role === "admin"} />}
      {canManage && <AddCandidateDialog session={candidateFor} onClose={() => { setCandidateFor(null); load(); }} />}
      {canManage && <SessionBulkDialog open={bulkOpen} setOpen={setBulkOpen} onSaved={load} />}
    </div>
  );
}

function SessionBulkDialog({ open, setOpen, onSaved }) {
  const [text, setText] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const r = new FileReader(); r.onload = () => setText(String(r.result || "")); r.readAsText(f);
  };
  const template = "course,date,start,end,join_link,location,mode,capacity,trainer\nSafeguarding Level 1,2026-07-15,09:30,12:30,https://zoom.us/j/123,Online,online,20,trainer@email.com\n";
  const downloadTemplate = () => {
    const blob = new Blob([template], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "sessions_template.csv"; a.click();
  };
  const upload = async () => {
    if (!text.trim()) return toast.error("Choose a CSV file first");
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/sessions/bulk", { csv: text });
      setResult(data);
      if (data.created) { toast.success(`${data.created} session(s) created`); onSaved(); }
      else toast.error("No sessions created — check the errors");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-heading">Bulk upload sessions</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">CSV columns: <span className="font-mono text-xs">course, date, start, end, join_link, location, mode, capacity, trainer</span>. Course can be the title or ID; trainer is their name or email.</p>
          <button onClick={downloadTemplate} className="text-sm text-primary hover:underline" data-testid="session-template-download">Download CSV template</button>
          <Input type="file" accept=".csv" onChange={onFile} data-testid="session-csv-input" />
          {result && (
            <div className="text-sm rounded-lg border border-border p-3 max-h-48 overflow-auto" data-testid="session-import-result">
              <p className="text-emerald-600 font-medium">{result.created} created</p>
              {result.errors?.length > 0 && <ul className="mt-1 list-disc pl-4 text-red-600">{result.errors.map((er, i) => <li key={i}>Row {er.row}: {er.message}</li>)}</ul>}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
          <Button onClick={upload} disabled={busy} data-testid="session-bulk-upload-button">{busy ? "Uploading…" : "Upload"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SessionChip({ s, onClick }) {
  const MIcon = modeIcon[s.mode] || Building2;
  const color = s.seats_left === 0 ? "bg-violet-500" : "bg-primary";
  return (
    <button onClick={onClick} data-testid={`session-chip-${s.id}`} className={`w-full text-left ${color} text-white rounded px-1.5 py-1 text-[11px] leading-tight hover:opacity-90 transition-opacity`}>
      <span className="flex items-center gap-1 font-medium"><MIcon className="h-3 w-3 shrink-0" /><span className="truncate">{s.course?.title}</span></span>
      <span className="opacity-80">{fmtTime(s.start)}</span>
    </button>
  );
}

function CalendarView({ sessions, cursor, setCursor, enrolledSessionIds, register, user, canManage, remove, onEdit, onAddCandidate, reload }) {
  const [selectedId, setSelectedId] = useState(null);
  const selected = sessions.find((x) => x.id === selectedId) || null;
  const setSelected = (s) => setSelectedId(s ? s.id : null);
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const first = new Date(year, month, 1);
  const startDay = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const byDay = {};
  sessions.forEach((s) => {
    const dt = new Date(s.start);
    if (dt.getFullYear() === year && dt.getMonth() === month) {
      const d = dt.getDate();
      (byDay[d] = byDay[d] || []).push(s);
    }
  });

  const today = new Date();

  return (
    <Card className="p-4 sm:p-6">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-heading text-xl font-semibold">{cursor.toLocaleDateString("en-GB", { month: "long", year: "numeric" })}</h3>
        <div className="flex gap-1">
          <Button variant="outline" size="icon" onClick={() => setCursor(new Date(year, month - 1, 1))} data-testid="session-calendar-prev-month-button"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => setCursor(new Date())}>Today</Button>
          <Button variant="outline" size="icon" onClick={() => setCursor(new Date(year, month + 1, 1))} data-testid="session-calendar-next-month-button"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground py-2">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          const isToday = d === today.getDate() && month === today.getMonth() && year === today.getFullYear();
          return (
            <div key={i} className={`min-h-[92px] rounded-lg border p-1.5 ${d ? "border-border" : "border-transparent"} ${isToday ? "bg-accent/40 border-primary/40" : ""}`}>
              {d && <div className={`text-xs font-medium mb-1 ${isToday ? "text-primary" : "text-muted-foreground"}`}>{d}</div>}
              <div className="space-y-1">
                {(byDay[d] || []).map((s) => <SessionChip key={s.id} s={s} onClick={() => setSelected(s)} />)}
              </div>
            </div>
          );
        })}
      </div>
      <SessionDetail s={selected} onClose={() => setSelected(null)} enrolled={selected && enrolledSessionIds.has(selected.id)} register={register} user={user} canManage={canManage} remove={(id) => { remove(id); setSelected(null); }}
        onEdit={(s) => { setSelected(null); onEdit(s); }} onAddCandidate={(s) => { setSelected(null); onAddCandidate(s); }} reload={reload} />
    </Card>
  );
}

function BulkBar({ ids, selected, setSelected, reload }) {
  const [busy, setBusy] = useState(false);
  const chosen = ids.filter((id) => selected.has(id));
  const allOn = ids.length > 0 && chosen.length === ids.length;
  const delSelected = async () => {
    if (!window.confirm(`Delete ${chosen.length} session(s)? All their registrations will be removed. This cannot be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/sessions/bulk-delete", { ids: chosen });
      toast.success(`${data.deleted} session(s) deleted`);
      setSelected(new Set());
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setBusy(false);
  };
  return (
    <div className="flex items-center gap-3 mb-3 px-1" data-testid="sessions-bulk-bar">
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <Checkbox checked={allOn} onCheckedChange={(v) => setSelected(v ? new Set(ids) : new Set())} data-testid="select-all-sessions-checkbox" />
        Select all ({ids.length})
      </label>
      {chosen.length > 0 && (
        <Button size="sm" variant="destructive" onClick={delSelected} disabled={busy} data-testid="delete-selected-sessions-button">
          <Trash2 className="h-4 w-4 mr-1.5" />Delete selected ({chosen.length})
        </Button>
      )}
    </div>
  );
}

function ListView({ sessions, enrolledSessionIds, register, user, canManage, remove, reload, onEdit, onAddCandidate }) {
  const [selected, setSelected] = useState(new Set());
  const upcoming = sessions.filter((s) => new Date(s.start) >= new Date(new Date().setHours(0, 0, 0, 0)));
  if (upcoming.length === 0) return <EmptyState message="No upcoming sessions scheduled." icon={CalendarDays} />;
  const toggle = (id, on) => setSelected((prev) => { const n = new Set(prev); on ? n.add(id) : n.delete(id); return n; });
  return (
    <div className="space-y-3">
      {canManage && <BulkBar ids={upcoming.map((s) => s.id)} selected={selected} setSelected={setSelected} reload={reload} />}
      {upcoming.map((s) => {
        const MIcon = modeIcon[s.mode] || Building2;
        const enrolled = enrolledSessionIds.has(s.id);
        return (
          <Card key={s.id} className={`p-5 flex flex-col sm:flex-row sm:items-center gap-4 stagger-in ${selected.has(s.id) ? "ring-2 ring-primary/40" : ""}`} data-testid={`session-row-${s.id}`}>
            {canManage && <Checkbox checked={selected.has(s.id)} onCheckedChange={(v) => toggle(s.id, !!v)} data-testid={`select-session-${s.id}-checkbox`} />}
            <div className="h-14 w-14 rounded-lg bg-primary/10 flex flex-col items-center justify-center shrink-0">
              <span className="text-[10px] uppercase text-primary font-semibold">{fmtDate(s.start, { month: "short" })}</span>
              <span className="font-heading font-bold text-xl text-primary leading-none">{new Date(s.start).getDate()}</span>
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-heading font-semibold">{s.course?.title}</h3>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{fmtTime(s.start)} – {fmtTime(s.end)}</span>
                <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{s.location}</span>
                <span className="flex items-center gap-1"><User className="h-3.5 w-3.5" />{s.trainer_name}</span>
                <span className="flex items-center gap-1 capitalize"><MIcon className="h-3.5 w-3.5" />{s.mode}</span>
              </div>
            </div>
            <div className="flex items-center gap-4 shrink-0">
              <div className="text-center">
                <p className="text-sm font-medium">{s.enrolled_count}/{s.capacity}</p>
                <p className="text-[11px] text-muted-foreground">{s.seats_left > 0 ? `${s.seats_left} left` : "Full"}</p>
              </div>
              {user.role === "employee" && (
                enrolled ? <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50 dark:bg-emerald-500/10">Registered</Badge>
                  : <Button size="sm" onClick={() => register(s.id)} data-testid={`register-session-${s.id}-button`}>{s.seats_left > 0 ? "Register" : "Join waitlist"}</Button>
              )}
              {canManage && (
                <>
                  <Button variant="outline" size="sm" onClick={() => onAddCandidate(s)} data-testid={`add-candidate-${s.id}-button`}><UserPlus className="h-4 w-4 mr-1" />Add candidate</Button>
                  <Button variant="ghost" size="icon" onClick={() => onEdit(s)} data-testid={`edit-session-${s.id}-button`} title="Edit session"><Pencil className="h-4 w-4" /></Button>
                </>
              )}
              {canManage && (
                <Button variant="ghost" size="icon" onClick={() => remove(s.id)} data-testid={`delete-session-${s.id}-button`} className="text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10" title="Delete session"><Trash2 className="h-4 w-4" /></Button>
              )}
            </div>
          </Card>
        );
      })}
    </div>
  );
}

function SessionDetail({ s, onClose, enrolled, register, user, canManage, remove, onEdit, onAddCandidate, reload }) {
  if (!s) return null;
  const MIcon = modeIcon[s.mode] || Building2;
  return (
    <Dialog open={!!s} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{s.course?.title}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-muted-foreground line-clamp-3" data-testid="session-course-synopsis">{s.course?.synopsis || stripHtml(s.course?.description)}</p>
          <div className="grid grid-cols-2 gap-3">
            <Detail icon={CalendarDays} label="Date" value={fmtDate(s.start)} />
            <Detail icon={Clock} label="Time" value={`${fmtTime(s.start)} – ${fmtTime(s.end)}`} />
            <Detail icon={MapPin} label="Location" value={s.location} />
            <Detail icon={MIcon} label="Mode" value={s.mode} />
            <Detail icon={User} label="Trainer" value={s.trainer_name} />
            <Detail icon={Users} label="Seats" value={`${s.enrolled_count}/${s.capacity}`} />
          </div>
          {s.join_link && <a href={s.join_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline" data-testid="session-join-link"><Video className="h-4 w-4" />Join link</a>}
          {s.waitlist_count > 0 && <p className="text-xs text-violet-600">{s.waitlist_count} on waitlist</p>}
          {canManage && <CandidateList sid={s.id} onChanged={reload} />}
        </div>
        <DialogFooter className="sm:justify-between gap-2">
          {canManage
            ? <Button variant="outline" onClick={() => remove(s.id)} data-testid="detail-delete-session-button" className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 dark:border-red-500/30 dark:hover:bg-red-500/10"><Trash2 className="h-4 w-4 mr-1.5" />Delete session</Button>
            : <span />}
          {canManage && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onAddCandidate(s)} data-testid="detail-add-candidate-button"><UserPlus className="h-4 w-4 mr-1.5" />Add candidate</Button>
              <Button onClick={() => onEdit(s)} data-testid="detail-edit-session-button"><Pencil className="h-4 w-4 mr-1.5" />Edit</Button>
            </div>
          )}
          {user.role === "employee" && (enrolled
            ? <Badge variant="outline" className="border-emerald-300 text-emerald-700">Already registered</Badge>
            : <Button onClick={() => { register(s.id); onClose(); }} data-testid="detail-register-button">{s.seats_left > 0 ? "Register now" : "Join waitlist"}</Button>)}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CandidateRow({ sid, r, onChanged }) {
  const [options, setOptions] = useState(null);
  const [target, setTarget] = useState("");
  const [courseId, setCourseId] = useState("");
  const [notify, setNotify] = useState(true);
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);
  const tid = r.session_id;
  const startMove = async () => {
    if (options) { setOptions(null); return; }
    const { data } = await api.get(`/sessions/${sid}/move-options`);
    setOptions(data);
    setCourseId(data.sessions.some((x) => x.course_id === data.current_course_id) ? data.current_course_id : "");
    setTarget("");
  };
  const courses = options ? Object.values(options.sessions.reduce((a, s) => { a[s.course_id] = a[s.course_id] || { id: s.course_id, title: s.course_title, type: s.course_type, price: s.course_price, n: 0 }; a[s.course_id].n += 1; return a; }, {})).sort((a, b) => a.title.localeCompare(b.title)) : [];
  const dates = options ? options.sessions.filter((s) => s.course_id === courseId) : [];
  const targetCourse = courses.find((c) => c.id === courseId);
  const online = targetCourse && targetCourse.type !== "live";
  const move = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/sessions/${sid}/candidates/${tid}/move`, { target_session_id: target, notify, course_link: online ? link.trim() || null : null });
      toast.success(`${r.candidate_name} ${data.course_changed ? "transferred" : "moved"}${data.link_sent ? " and sent their course link" : data.emailed ? " and emailed the new details" : ""}`);
      if (data.needs_link) toast.warning(`Remember to send ${r.candidate_name}'s personal course link from Sales (Send link) - this is an online course.`, { duration: 10000 });
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setBusy(false);
  };
  const removeIt = async () => {
    if (!window.confirm(`Remove ${r.candidate_name} from this session?`)) return;
    try {
      await api.delete(`/sessions/${sid}/candidates/${tid}`);
      toast.success(`${r.candidate_name} removed`);
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  return (
    <li className="text-xs py-1 border-b border-border last:border-0" data-testid={`candidate-row-${tid}`}>
      <div className="flex items-center gap-2">
        <span className="font-medium">{r.candidate_name}</span>
        <span className="text-muted-foreground truncate flex-1">{r.candidate_email}</span>
        <button onClick={startMove} className="p-1 rounded hover:bg-accent/50" title="Transfer to another date or course" data-testid={`candidate-move-${tid}-button`}><ArrowRightLeft className="h-3.5 w-3.5" /></button>
        <button onClick={removeIt} className="p-1 rounded hover:bg-red-50 text-red-600" title="Remove from session" data-testid={`candidate-remove-${tid}-button`}><X className="h-3.5 w-3.5" /></button>
      </div>
      {options && (
        <div className="mt-2 space-y-2 rounded-md bg-muted/40 p-2.5" data-testid={`candidate-move-panel-${tid}`}>
          <p className="font-medium">Transfer {r.candidate_name} to:</p>
          <Select value={courseId} onValueChange={(v) => { setCourseId(v); setTarget(""); }}>
            <SelectTrigger className="h-8 text-xs bg-background" data-testid={`candidate-move-course-${tid}`}><SelectValue placeholder="Choose course" /></SelectTrigger>
            <SelectContent>{courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.title} · {c.type === "live" ? "Live" : "Online"}{c.price != null ? ` · £${c.price}` : ""}{c.id === options.current_course_id ? " (same course)" : ""} · {c.n} date(s)</SelectItem>)}</SelectContent>
          </Select>
          {!courseId ? <p className="text-muted-foreground">No other upcoming dates for this course - choose another course above.</p> : dates.length === 0
            ? <p className="text-muted-foreground">No other upcoming dates for this course - choose another course above.</p>
            : (
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger className="h-8 text-xs bg-background" data-testid={`candidate-move-select-${tid}`}><SelectValue placeholder="Choose new date" /></SelectTrigger>
                <SelectContent>{dates.map((o) => <SelectItem key={o.id} value={o.id} disabled={o.seats_left === 0}>{fmtDate(o.start)} {fmtTime(o.start)} · {o.location}{o.seats_left != null ? ` · ${o.seats_left === 0 ? "Full" : `${o.seats_left} seats left`}` : ""}</SelectItem>)}</SelectContent>
              </Select>
            )}
          {online && target && (
            <div>
              <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="Paste their personal course link (recommended)" className="h-8 text-xs bg-background" data-testid={`candidate-move-link-${tid}`} />
              <p className="text-muted-foreground mt-1">Online course: the link is emailed to the learner now. Leave blank to send it later from Sales.</p>
            </div>
          )}
          {target && <p className="text-primary font-medium" data-testid={`candidate-move-summary-${tid}`}>Moving to: {targetCourse?.title} - {fmtDate(dates.find((d) => d.id === target)?.start)} {fmtTime(dates.find((d) => d.id === target)?.start)}</p>}
          {courseId && courseId !== options.current_course_id && <p className="text-amber-700">Changing course keeps the same booking and payment - any price difference isn't charged or refunded automatically.</p>}
          <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid={`candidate-move-notify-${tid}`} />Email the learner their new course details</label>
          <div className="flex gap-2">
            <Button size="sm" className="h-8" disabled={!target || busy} onClick={move} data-testid={`candidate-move-confirm-${tid}`}>{busy ? "Transferring..." : "Transfer"}</Button>
            <Button size="sm" variant="ghost" className="h-8" onClick={() => setOptions(null)}>Cancel</Button>
          </div>
        </div>
      )}
    </li>
  );
}

function CandidateList({ sid, onChanged }) {
  const [rows, setRows] = useState(null);
  const load = () => api.get(`/sessions/${sid}/candidates`).then(({ data }) => setRows(data)).catch(() => setRows([]));
  const changed = () => { load(); onChanged?.(); };
  useEffect(() => { load(); }, [sid]);
  if (!rows) return null;
  return (
    <div className="rounded-lg border border-border p-3" data-testid="session-candidates-list">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Booked candidates ({rows.length})</p>
      {rows.length === 0 ? <p className="text-xs text-muted-foreground">No candidates booked yet.</p> : (
        <ul className="max-h-56 overflow-auto">
          {rows.map((r) => <CandidateRow key={r.session_id} sid={sid} r={r} onChanged={changed} />)}
        </ul>
      )}
    </div>
  );
}

const blankRow = (email = "") => ({ name: "", email, error: "" });

function CandidateRowInputs({ row, i, update, remove, canRemove }) {
  return (
    <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-start" data-testid={`candidate-row-input-${i}`}>
      <div>
        <Input value={row.name} onChange={(e) => update(i, { name: e.target.value, error: "" })} placeholder="Full name" data-testid={`candidate-name-input-${i}`} />
        {row.error && <p className="text-xs text-red-600 mt-1" data-testid={`candidate-row-error-${i}`}>{row.error}</p>}
      </div>
      <Input type="email" value={row.email} onChange={(e) => update(i, { email: e.target.value, error: "" })} placeholder="Email address" data-testid={`candidate-email-input-${i}`} />
      <Button variant="ghost" size="icon" onClick={() => remove(i)} disabled={!canRemove} title="Remove row" data-testid={`candidate-row-remove-${i}`}><X className="h-4 w-4" /></Button>
    </div>
  );
}

function AddCandidateDialog({ session, onClose }) {
  const [rows, setRows] = useState([blankRow()]);
  const [sendEmail, setSendEmail] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (session) { setRows([blankRow()]); setSendEmail(true); } }, [session]);
  if (!session) return null;
  const update = (i, patch) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const remove = (i) => setRows((rs) => rs.filter((_, j) => j !== i));
  const addRow = () => setRows((rs) => [...rs, blankRow(rs[rs.length - 1]?.email || "")]);

  const save = async () => {
    const filled = rows.filter((r) => r.name.trim() || r.email.trim());
    if (filled.length === 0 || filled.some((r) => !r.name.trim() || !r.email.trim())) return toast.error("Every candidate needs a name and email");
    setSaving(true);
    const failed = [];
    let added = 0;
    for (const r of filled) {
      try {
        await api.post(`/sessions/${session.id}/candidates`, { name: r.name, email: r.email, send_email: sendEmail });
        added += 1;
      } catch (e) {
        failed.push({ ...r, error: formatApiErrorDetail(e.response?.data?.detail) });
      }
    }
    setSaving(false);
    if (added) toast.success(`${added} candidate${added > 1 ? "s" : ""} added to the session`);
    if (failed.length) {
      toast.error(`${failed.length} could not be added — see below`);
      setRows(failed);
    } else onClose();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-candidate-dialog">
        <DialogHeader><DialogTitle className="font-heading">Add candidates</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">{session.course?.title} · {fmtDate(session.start)} {fmtTime(session.start)}</p>
        <div className="space-y-2">
          <div className="grid grid-cols-[1fr_1fr_auto] gap-2 text-xs font-medium text-muted-foreground"><span>Full name</span><span>Email (can be shared)</span><span className="w-9" /></div>
          {rows.map((r, i) => <CandidateRowInputs key={i} row={r} i={i} update={update} remove={remove} canRemove={rows.length > 1} />)}
          <Button variant="outline" size="sm" onClick={addRow} data-testid="candidate-add-row-button"><Plus className="h-4 w-4 mr-1" />Add another candidate</Button>
          <label className="flex items-center gap-2 text-sm cursor-pointer pt-2">
            <Checkbox checked={sendEmail} onCheckedChange={(v) => setSendEmail(!!v)} data-testid="candidate-send-email-checkbox" />
            Email each candidate their course access link now
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="candidate-save-button">{saving ? "Adding..." : `Add ${rows.length > 1 ? `${rows.length} candidates` : "candidate"}`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Detail({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="h-4 w-4 text-muted-foreground mt-0.5" />
      <div><p className="text-xs text-muted-foreground">{label}</p><p className={`font-medium ${label === "Mode" ? "capitalize" : ""}`}>{value}</p></div>
    </div>
  );
}

const blankSession = { course_id: "", trainer_id: "", date: "", time: "09:00", duration: 2, location: "Room 302", mode: "classroom", capacity: 20, join_link: "" };
const pad = (n) => String(n).padStart(2, "0");

function toForm(s) {
  const st = new Date(s.start);
  const en = new Date(s.end);
  return {
    course_id: s.course_id, trainer_id: s.trainer_id || "", date: `${st.getFullYear()}-${pad(st.getMonth() + 1)}-${pad(st.getDate())}`,
    time: `${pad(st.getHours())}:${pad(st.getMinutes())}`, duration: Math.round(((en - st) / 3600000) * 100) / 100,
    location: s.location || "", mode: s.mode || "classroom", capacity: s.capacity ?? 20, join_link: s.join_link || "",
  };
}

function SessionDialog({ open, setOpen, session, courses, trainers, onSaved, isAdmin }) {
  const [form, setForm] = useState(blankSession);
  useEffect(() => { if (open) { setForm(session ? toForm(session) : blankSession); } }, [open, session]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const pickCourse = (id) => setForm((f) => {
    const prev = courses.find((c) => c.id === f.course_id)?.join_link || "";
    const next = courses.find((c) => c.id === id)?.join_link || "";
    const keep = f.join_link && f.join_link !== prev;
    return { ...f, course_id: id, join_link: keep ? f.join_link : next };
  });

  const save = async () => {
    if (!form.course_id || !form.date) return toast.error("Course and date are required");
    setSaving(true);
    try {
      const start = new Date(`${form.date}T${form.time}`);
      const end = new Date(start.getTime() + form.duration * 3600000);
      const payload = {
        course_id: form.course_id, trainer_id: form.trainer_id || null,
        start: start.toISOString(), end: end.toISOString(),
        location: form.location, mode: form.mode, capacity: Number(form.capacity),
        join_link: form.join_link || null,
      };
      if (session) await api.put(`/sessions/${session.id}`, payload);
      else await api.post("/sessions", payload);
      toast.success(session ? "Session updated" : "Session scheduled");
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-heading">{session ? "Edit Session" : "Schedule Session"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Course</Label>
            <CourseCombobox courses={courses} value={form.course_id} onChange={pickCourse} testid="session-course-select" />
          </div>
          {isAdmin && (
            <div>
              <Label>Trainer</Label>
              <Select value={form.trainer_id} onValueChange={(v) => set("trainer_id", v)}>
                <SelectTrigger className="mt-1.5" data-testid="session-trainer-select"><SelectValue placeholder="Assign trainer" /></SelectTrigger>
                <SelectContent>{trainers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className="mt-1.5" data-testid="session-date-input" /></div>
            <div><Label>Start time</Label><Input type="time" value={form.time} onChange={(e) => set("time", e.target.value)} className="mt-1.5" /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Hours</Label><Input type="number" value={form.duration} onChange={(e) => set("duration", Number(e.target.value))} className="mt-1.5" /></div>
            <div className="col-span-2">
              <Label>Mode</Label>
              <Select value={form.mode} onValueChange={(v) => set("mode", v)}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{["online", "classroom", "blended", "virtual"].map((m) => <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Location</Label><Input value={form.location} onChange={(e) => set("location", e.target.value)} className="mt-1.5" data-testid="session-location-input" /></div>
            <div><Label>Capacity</Label><Input type="number" value={form.capacity} onChange={(e) => set("capacity", e.target.value)} className="mt-1.5" /></div>
          </div>
          <div><Label>Join link <span className="text-muted-foreground font-normal">(optional)</span></Label><Input value={form.join_link} onChange={(e) => set("join_link", e.target.value)} placeholder="https://zoom.us/j/…" className="mt-1.5" data-testid="session-join-link-input" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-session-button">{saving ? "Saving..." : session ? "Save changes" : "Schedule"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
